import React, { useEffect, useState } from "react";
import { ApiConstants } from "../../constants/ApiConstants";
import useMakeRequest from "../../utils/ApiHelper";
import { PAGINATION } from "../../constants/Constants";
import { useNavigate } from "react-router-dom";
import BrandedLoaderOverlay from "../layout/BrandedLoaderOverlay";
import { useResourceEverLoaded } from "../layout/useSmartBrandedLoader";
import { LanguageConstants } from "../../constants/LanguageConstants";
import { ProfileAction } from "../../redux/Action/Authaction";
import { useDispatch } from "react-redux";
import "./category-select.scss";

const formatCategoryLabel = (value: any): string => {
  const source = String(value ?? "").trim();
  if (!source) return "";
  return source
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

function Category() {
  const [data, setData] = useState<any[]>([]);
  const categoryList = useMakeRequest();
  const categoryAdd = useMakeRequest();
  const navigate = useNavigate();
  const [select_ids, setSelect_ids] = useState<any[]>([]);
  const dispatch = useDispatch();
  const [pagination, setPagination] = useState({
    page: PAGINATION.PAGE || 0,
    pageSize: PAGINATION.PAGESIZE || 10,
  });

  useEffect(() => {
    const payload = {
      page: pagination?.page,
      pageSize: pagination?.pageSize,
    };
    categoryList.makeRequest({
      payload,
      url: ApiConstants?.HomeCategory,
      toastStatus: false,
    });
  }, [pagination]);

  useEffect(() => {
    if (categoryList.status) {
      const rows = categoryList?.data?.data?.rows || [];
      const defaultIdSet = new Set(
        (categoryList?.data?.data?.default_cat_ids || [])
          .map((id: any) => Number(id))
          .filter((id: number) => Number.isFinite(id)),
      );
      // Hide fixed default onboarding category from selection UI only.
      // Skip / empty submit still assigns it server-side via `skip: true`.
      setData(
        rows.filter((row: any) => {
          const id = Number(row?.id);
          if (Number(row?.default_onboarding) === 1) return false;
          if (Number.isFinite(id) && defaultIdSet.has(id)) return false;
          return true;
        }),
      );
    }
  }, [categoryList?.status]);

  useEffect(() => {
    if (
      categoryList?.data?.data?.count >= 10 &&
      pagination?.pageSize < categoryList?.data?.data?.count
    ) {
      const handleScroll = () => {
        if (
          window.innerHeight + window.scrollY >=
          document.body.offsetHeight - 100
        ) {
          setPagination((prevPagination) => ({
            ...prevPagination,
            pageSize:
              prevPagination.pageSize < 20 ? 20 : prevPagination.pageSize,
          }));
        }
      };
      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [pagination, categoryList?.data?.data?.count]);

  const handleSelect = (id: number) => {
    setSelect_ids((prev: any[]) => {
      if (prev.includes(id)) {
        return prev.filter((item: number) => item !== id);
      }
      return [...prev, id];
    });
  };

  const handelSubmit = () => {
    if (select_ids?.length === 0) {
      categoryAdd.makeRequest({
        payload: { skip: true },
        url: ApiConstants?.AddCategory,
        toastStatus: false,
      });
      return;
    }
    categoryAdd.makeRequest({
      payload: { cat_ids: select_ids },
      url: ApiConstants?.AddCategory,
      toastStatus: true,
    });
  };

  const handleSkip = () => {
    categoryAdd.makeRequest({
      payload: { skip: true },
      url: ApiConstants?.AddCategory,
      toastStatus: false,
    });
  };

  useEffect(() => {
    if (categoryAdd?.status) {
      // @ts-ignore
      dispatch(ProfileAction());
      setTimeout(() => {
        navigate("/");
      }, 1500);
    }
  }, [categoryAdd?.status, dispatch, navigate]);

  const categoryListEverLoaded = useResourceEverLoaded(categoryList?.data?.data != null);
  const showCategoryLoader =
    Boolean(categoryAdd?.loading) ||
    (categoryList.loading && !categoryListEverLoaded);

  return (
    <>
      <BrandedLoaderOverlay active={showCategoryLoader} />
      <main className="category-select-main">
        <div className="footer-space">
          <div className="category-select-shell">
            <div className="category-select-card">
              <div className="category-select-card__hero">
                <button
                  type="button"
                  className="category-select-skip-btn"
                  onClick={handleSkip}
                >
                  {LanguageConstants?.SKIP}
                </button>
                <div className="category-select-card__titles">
                  <h1 className="category-select-card__title">
                    {LanguageConstants?.SELECT_CATEGORY_TITLE}
                  </h1>
                  <p className="category-select-card__sub">
                    {LanguageConstants?.SELECT_CATEGORY_SUBLINE_1}
                  </p>
                  <p className="category-select-card__sub">
                    {LanguageConstants?.SELECT_CATEGORY_SUBLINE_2}
                  </p>
                </div>
              </div>

              <div className="category-select-card__body">
                <div className="category-select-grid">
                  {data.length > 0 ? (
                    data.map((res: any, i: number) => (
                      <button
                        type="button"
                        key={res?.id ?? i}
                        className={
                          select_ids?.includes(res?.id)
                            ? "category-select-pill is-active"
                            : "category-select-pill"
                        }
                        onClick={() => handleSelect(res?.id)}
                      >
                        {formatCategoryLabel(res?.category_details?.name)}
                      </button>
                    ))
                  ) : (
                    <p className="category-select-grid__empty">{LanguageConstants?.NO_DATA}</p>
                  )}
                </div>

                <div className="category-select-card__actions">
                  <button
                    type="button"
                    className="category-select-continue"
                    onClick={handelSubmit}
                  >
                    {LanguageConstants?.COUNTINUE}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default Category;
